import { toValue, watch } from "vue";

// Used when the computed line-height resolves to `normal`, which carries no length.
const NORMAL_LINE_HEIGHT_RATIO = 1.2;

/**
 * Matches the field height to its content while `autoGrow` is on, never below
 * `rows` and never past `maxRows`.
 *
 * @param {import('vue').Ref<HTMLTextAreaElement|null>} textarea
 * @param {Object} sources
 * @param {import('vue').MaybeRefOrGetter<Boolean>} sources.autoGrow
 * @param {import('vue').MaybeRefOrGetter<Number>} sources.rows
 * @param {import('vue').MaybeRefOrGetter<Number|undefined>} sources.maxRows
 * @param {import('vue').MaybeRefOrGetter<String>} sources.text
 * @returns {{ resize: () => void }}
 */
export function useTextareaAutoGrow(textarea, { autoGrow, rows, maxRows, text }) {
  // The template ref carries the element in as it mounts, which is the first measurement.
  watch([textarea, () => toValue(text), () => toValue(autoGrow), () => toValue(rows), () => toValue(maxRows)], resize, {
    flush: "post",
  });

  function resize() {
    const element = textarea.value;

    if (!element) {
      return;
    }

    if (!toValue(autoGrow)) {
      element.style.height = "";
      element.style.overflowY = "";
      return;
    }

    // `auto` restores the rows-driven height, so scrollHeight reads the content
    // height with the rows minimum already applied.
    element.style.height = "auto";

    const cap = maxHeight(element);
    const contentHeight = element.scrollHeight;
    const height = cap === undefined ? contentHeight : Math.min(contentHeight, cap);

    element.style.height = `${height}px`;
    element.style.overflowY = contentHeight > height ? "auto" : "hidden";
  }

  function maxHeight(element) {
    const cappedAt = toValue(maxRows);

    if (!cappedAt) {
      return undefined;
    }

    const style = getComputedStyle(element);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * NORMAL_LINE_HEIGHT_RATIO;
    const cappedRows = Math.max(cappedAt, toValue(rows));
    const height = lineHeight * cappedRows + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);

    return Number.isFinite(height) ? height : undefined;
  }

  return { resize };
}
